import React, { useMemo, useState } from 'react';
import { MarketPricesTimeSpan } from '../../../../Generated/Raven';
import { DemeterFilterTimeSpan } from '../../../../Generated/Raven-Demeter';
import timeSpanControlsImage from '../../../Assets/Images/exampleTimeSpanControls.svg';
import timeSpanOptionsImage from '../../../Assets/Images/exampleTimeSpanOptions.svg';
import CardAccordion from '../../../Components/Form/Buttons/Accordions/CardAccordion';
import Switch from '../../../Components/Form/Buttons/Switch';
import CheckboxDropdown from '../../../Components/Form/Inputs/CheckboxDropdown';
import useLanguage from '../../../Services/Language/useLanguageHook';
import styles from '../ChartBuilder.module.scss';
import { ChartBuilderRequest } from '../ChartBuilderDefinitions';

interface IChartBuilderChartOptionsSectionProps {
    chartBuilderUpsertRequest?: ChartBuilderRequest;
    setChartBuilderUpsertRequest: (chartBuilderRequest: ChartBuilderRequest) => void;
}

const minimumSelectionLimit = 1;
const defaultFilterTimeSpan = DemeterFilterTimeSpan.All as MarketPricesTimeSpan;

const ChartBuilderChartOptionsSection: React.FC<IChartBuilderChartOptionsSectionProps> = (props: IChartBuilderChartOptionsSectionProps) => {
    // Application hooks.
    const [translations] = useLanguage();

    // Switch hooks.
    const [useTimeSpanOptions, setUseTimeSpanOptions] = useState(!!props.chartBuilderUpsertRequest?.showTimespanOptions);

    // Dropdown options.
    const timeSpanOptions = useMemo(
        () => [
            {
                label: translations.filterTimeSpan[DemeterFilterTimeSpan.SixMonths],
                value: DemeterFilterTimeSpan.SixMonths,
            },
            {
                label: translations.filterTimeSpan[DemeterFilterTimeSpan.OneYear],
                value: DemeterFilterTimeSpan.OneYear,
            },
            {
                label: translations.filterTimeSpan[DemeterFilterTimeSpan.TwoYears],
                value: DemeterFilterTimeSpan.TwoYears,
            },
            {
                label: translations.filterTimeSpan[DemeterFilterTimeSpan.FiveYears],
                value: DemeterFilterTimeSpan.FiveYears,
            },
            {
                label: translations.filterTimeSpan[DemeterFilterTimeSpan.All],
                value: DemeterFilterTimeSpan.All,
            },
        ],
        [translations],
    );

    return (
        <CardAccordion
            type="gray"
            title={translations.chartBuilder.headers.chartOptions}
            component={
                <>
                    <div className={styles.chart_builder_row} />
                    <div className={styles.chart_builder_timespan_row}>
                        <p className={styles.chart_builder_label}>{translations.chartBuilder.fields.timespanOptions}</p>
                        <Switch
                            checked={!!props.chartBuilderUpsertRequest?.showTimespanOptions}
                            handleChange={(useTimespan) => {
                                props.setChartBuilderUpsertRequest({
                                    ...props.chartBuilderUpsertRequest!,
                                    showTimespanOptions: useTimespan,
                                });
                                setUseTimeSpanOptions(useTimespan);
                            }}
                            track={{
                                eventCategory: 'ChartBuilder',
                                eventAction: 'ShowTimespanOptionsChanged',
                            }}
                        />
                    </div>
                    <div className={styles.chart_builder_options_offset}>
                        <img className={styles.chart_builder_timespan_options_image} src={timeSpanOptionsImage} alt="" />
                        <p className={styles.chart_builder_options_offset_p}>{translations.chartBuilder.text.exampleTimespanOptions}</p>
                    </div>
                    {useTimeSpanOptions && (
                        <div className={styles.chart_builder_options_offset}>
                            <CheckboxDropdown
                                placeholder={props.chartBuilderUpsertRequest?.timespanOptions?.length === 0 ? translations.words.select : ''}
                                options={timeSpanOptions}
                                labelType="removableElements"
                                minimumSelectionLimit={minimumSelectionLimit}
                                label={translations.chartBuilder.fields.timespanValues}
                                values={props.chartBuilderUpsertRequest?.timespanOptions!}
                                handleOptionSelect={(value) => {
                                    const availableOptions = timeSpanOptions.filter((x) => value.includes(x.value)).map((x) => x.value)!;
                                    props.setChartBuilderUpsertRequest({
                                        ...props.chartBuilderUpsertRequest!,
                                        timespanOptions: availableOptions,
                                        rows: props.chartBuilderUpsertRequest!.rows.map((x) => ({
                                            ...x,
                                            filterTimeSpan: availableOptions[0] as DemeterFilterTimeSpan,
                                            sources: x.sources.map((y) => ({ ...y, filterTimeSpan: availableOptions[0] as DemeterFilterTimeSpan })),
                                        })),
                                    });
                                }}
                                track={{
                                    eventCategory: 'ChartBuilder',
                                    eventAction: 'TimespanOptionsChanged',
                                    eventValue: props.chartBuilderUpsertRequest?.timespanOptions?.join(',') || '',
                                }}
                            />
                        </div>
                    )}
                    <div className={styles.chart_builder_timespan_space} />
                    <div className={styles.chart_builder_timespan_row}>
                        <p className={styles.chart_builder_label}>{translations.chartBuilder.fields.timespanControls}</p>
                        <Switch
                            checked={!!props.chartBuilderUpsertRequest?.showTimespanControls}
                            handleChange={(useTimespan) => {
                                props.setChartBuilderUpsertRequest({
                                    ...props.chartBuilderUpsertRequest!,
                                    showTimespanControls: useTimespan,
                                });
                            }}
                            track={{
                                eventCategory: 'ChartBuilder',
                                eventAction: 'ShowTimespanControlsChanged',
                            }}
                        />
                    </div>
                    <div className={styles.chart_builder_options_offset}>
                        <img className={styles.chart_builder_timespan_controls_image} src={timeSpanControlsImage} alt="" />
                        <p className={styles.chart_builder_options_offset_p}>{translations.chartBuilder.text.exampleTimespanControl}</p>
                    </div>
                </>
            }
        />
    );
};

export default ChartBuilderChartOptionsSection;
